/**
 * SVG path data → normalised absolute segments (M, L, C, Z only; arcs and quadratics become
 * cubics). Normalised paths can be transformed by any affine matrix exactly, which the PDF,
 * SVG and (later) DXF exporters rely on.
 */
import { apply, type Matrix } from './matrix.ts';

export type Segment =
  | { t: 'M'; x: number; y: number }
  | { t: 'L'; x: number; y: number }
  | { t: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { t: 'Z' };

const TOKEN = /([MLHVCSQTAZmlhvcsqtaz])|(-?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/g;

export class PathError extends Error {}

export function parsePath(d: string): Segment[] {
  const tokens: Array<string | number> = [];
  for (const m of d.matchAll(TOKEN)) tokens.push(m[1] ?? Number(m[2]));
  const out: Segment[] = [];
  let i = 0;
  let cmd = '';
  let cx = 0;
  let cy = 0;
  let sx = 0;
  let sy = 0;
  let lastC: { x: number; y: number } | null = null; // reflection point for S
  let lastQ: { x: number; y: number } | null = null; // reflection point for T
  const num = () => {
    const v = tokens[i++];
    if (typeof v !== 'number') throw new PathError(`number expected in "${d}"`);
    return v;
  };
  const flag = () => {
    // Flags must be separated by spaces or commas (compact "110" forms are not supported).
    const v = num();
    if (v !== 0 && v !== 1) throw new PathError(`arc flag must be 0 or 1 in "${d}"`);
    return v;
  };
  while (i < tokens.length) {
    const tk = tokens[i];
    if (typeof tk === 'string') {
      cmd = tk;
      i++;
    } else if (!cmd) throw new PathError(`path must start with a command: "${d}"`);
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    const ox = rel ? cx : 0;
    const oy = rel ? cy : 0;
    switch (C) {
      case 'M': {
        cx = ox + num();
        cy = oy + num();
        sx = cx;
        sy = cy;
        out.push({ t: 'M', x: cx, y: cy });
        cmd = rel ? 'l' : 'L'; // further pairs are line-tos
        lastC = lastQ = null;
        break;
      }
      case 'L':
        cx = ox + num();
        cy = oy + num();
        out.push({ t: 'L', x: cx, y: cy });
        lastC = lastQ = null;
        break;
      case 'H':
        cx = ox + num();
        out.push({ t: 'L', x: cx, y: cy });
        lastC = lastQ = null;
        break;
      case 'V':
        cy = oy + num();
        out.push({ t: 'L', x: cx, y: cy });
        lastC = lastQ = null;
        break;
      case 'C': {
        const x1 = ox + num();
        const y1 = oy + num();
        const x2 = ox + num();
        const y2 = oy + num();
        cx = ox + num();
        cy = oy + num();
        out.push({ t: 'C', x1, y1, x2, y2, x: cx, y: cy });
        lastC = { x: x2, y: y2 };
        lastQ = null;
        break;
      }
      case 'S': {
        const x1 = lastC ? 2 * cx - lastC.x : cx;
        const y1 = lastC ? 2 * cy - lastC.y : cy;
        const x2 = ox + num();
        const y2 = oy + num();
        cx = ox + num();
        cy = oy + num();
        out.push({ t: 'C', x1, y1, x2, y2, x: cx, y: cy });
        lastC = { x: x2, y: y2 };
        lastQ = null;
        break;
      }
      case 'Q':
      case 'T': {
        const qx: number = C === 'Q' ? ox + num() : lastQ ? 2 * cx - lastQ.x : cx;
        const qy: number = C === 'Q' ? oy + num() : lastQ ? 2 * cy - lastQ.y : cy;
        const x = ox + num();
        const y = oy + num();
        out.push({
          t: 'C',
          x1: cx + (2 / 3) * (qx - cx),
          y1: cy + (2 / 3) * (qy - cy),
          x2: x + (2 / 3) * (qx - x),
          y2: y + (2 / 3) * (qy - y),
          x,
          y,
        });
        cx = x;
        cy = y;
        lastQ = { x: qx, y: qy };
        lastC = null;
        break;
      }
      case 'A': {
        const rx = num();
        const ry = num();
        const rot = num();
        const large = flag();
        const sweep = flag();
        const x = ox + num();
        const y = oy + num();
        out.push(...arcToCubics(cx, cy, rx, ry, rot, large === 1, sweep === 1, x, y));
        cx = x;
        cy = y;
        lastC = lastQ = null;
        break;
      }
      case 'Z':
        out.push({ t: 'Z' });
        cx = sx;
        cy = sy;
        lastC = lastQ = null;
        break;
      default:
        throw new PathError(`unsupported command ${cmd}`);
    }
  }
  return out;
}

/** SVG arc (endpoint parameterisation) → cubic Béziers, ≤ 90° each. */
function arcToCubics(
  x1: number,
  y1: number,
  rx: number,
  ry: number,
  rotDeg: number,
  large: boolean,
  sweep: boolean,
  x2: number,
  y2: number,
): Segment[] {
  if ((x1 === x2 && y1 === y2) || rx === 0 || ry === 0) return [{ t: 'L', x: x2, y: y2 }];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const phi = (rotDeg * Math.PI) / 180;
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy;
  const y1p = -sin * dx + cos * dy;
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  let co = Math.sqrt(Math.max(0, num / den));
  if (large === sweep) co = -co;
  const cxp = (co * rx * y1p) / ry;
  const cyp = (-co * ry * x1p) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2;
  const cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const angle = (ux: number, uy: number, vx: number, vy: number) => {
    const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return a;
  };
  const th1 = angle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dth = angle((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dth > 0) dth -= 2 * Math.PI;
  if (sweep && dth < 0) dth += 2 * Math.PI;
  const n = Math.max(1, Math.ceil(Math.abs(dth) / (Math.PI / 2) - 1e-9));
  const delta = dth / n;
  const k = (4 / 3) * Math.tan(delta / 4);
  const segs: Segment[] = [];
  const pt = (t: number) => ({
    x: cx + rx * Math.cos(t) * cos - ry * Math.sin(t) * sin,
    y: cy + rx * Math.cos(t) * sin + ry * Math.sin(t) * cos,
  });
  const dpt = (t: number) => ({
    x: -rx * Math.sin(t) * cos - ry * Math.cos(t) * sin,
    y: -rx * Math.sin(t) * sin + ry * Math.cos(t) * cos,
  });
  let t = th1;
  for (let s = 0; s < n; s++) {
    const t2 = t + delta;
    const p1 = pt(t);
    const p2 = pt(t2);
    const d1 = dpt(t);
    const d2 = dpt(t2);
    const end = s === n - 1 ? { x: x2, y: y2 } : p2;
    segs.push({
      t: 'C',
      x1: p1.x + k * d1.x,
      y1: p1.y + k * d1.y,
      x2: p2.x - k * d2.x,
      y2: p2.y - k * d2.y,
      ...end,
    });
    t = t2;
  }
  return segs;
}

export function transformSegments(segs: readonly Segment[], m: Matrix): Segment[] {
  return segs.map((s) => {
    switch (s.t) {
      case 'Z':
        return s;
      case 'M':
      case 'L':
        return { t: s.t, ...apply(m, s.x, s.y) };
      case 'C': {
        const a = apply(m, s.x1, s.y1);
        const b = apply(m, s.x2, s.y2);
        const c = apply(m, s.x, s.y);
        return { t: 'C', x1: a.x, y1: a.y, x2: b.x, y2: b.y, x: c.x, y: c.y };
      }
    }
  });
}

const f = (n: number) => {
  const r = Math.round(n * 1000) / 1000;
  return Object.is(r, -0) ? '0' : String(r);
};

export function segmentsToPath(segs: readonly Segment[]): string {
  return segs
    .map((s) =>
      s.t === 'Z'
        ? 'Z'
        : s.t === 'C'
          ? `C${f(s.x1)} ${f(s.y1)} ${f(s.x2)} ${f(s.y2)} ${f(s.x)} ${f(s.y)}`
          : `${s.t}${f(s.x)} ${f(s.y)}`,
    )
    .join(' ');
}

/** Conservative bounds (Bézier control points included). */
export function segmentsBounds(segs: readonly Segment[]) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const add = (x: number, y: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const s of segs) {
    if (s.t === 'Z') continue;
    add(s.x, s.y);
    if (s.t === 'C') {
      add(s.x1, s.y1);
      add(s.x2, s.y2);
    }
  }
  return { minX, minY, maxX, maxY };
}

const cache = new Map<string, Segment[]>();
/** Parsed, cached path (symbol paths are reused thousands of times). */
export function normalisedPath(d: string): Segment[] {
  let s = cache.get(d);
  if (!s) {
    s = parsePath(d);
    cache.set(d, s);
  }
  return s;
}
